import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, ImagePlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import api, { resolveImage, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";

const inputCls =
  "w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition bg-white";
const labelCls = "block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2";

const EMPTY = {
  title: "", type: "casa", city: "", neighborhood: "", price: "",
  finalidade: "venda", condominio: "", iptu: "",
  area_privativa: "", area_total: "",
  bedrooms: 0, suites: 0, bathrooms: 0, parking: 0, description: "", featuresText: "",
  status: "disponivel", featured: false, published: true, map_url: "",
};

export default function PropertyForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { refreshProperties } = useSite();
  const [form, setForm] = useState(EMPTY);
  const [images, setImages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (user === null) navigate("/admin/login");
  }, [user, navigate]);

  useEffect(() => {
    if (isEdit && user) {
      api.get("/admin/properties").then(({ data }) => {
        const p = data.find((i) => i.id === id);
        if (!p) {
          toast.error("Imóvel não encontrado.");
          navigate("/admin");
          return;
        }
        setForm({
          title: p.title, type: p.type, city: p.city, neighborhood: p.neighborhood,
          price: p.price ?? "",
finalidade: p.finalidade ?? "venda",
condominio: p.condominio ?? "",
iptu: p.iptu ?? "",
area_privativa: p.area_privativa ?? "",
area_total: p.area_total ?? "",
          bedrooms: p.bedrooms, suites: p.suites, bathrooms: p.bathrooms, parking: p.parking,
          description: p.description, featuresText: (p.features || []).join("\n"),
          status: p.status, featured: p.featured, published: p.published, map_url: p.map_url || "",
        });
        setImages(p.images || []);
      });
    }
  }, [isEdit, id, user, navigate]);

  const set = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      type: form.type,
      city: form.city.trim(),
      neighborhood: form.neighborhood.trim(),
      price: form.price === "" ? null : Number(form.price),
      finalidade: form.finalidade,
      condominio: form.condominio === "" ? null : Number(form.condominio),
      iptu: form.iptu === "" ? null : Number(form.iptu),
      area_privativa: form.area_privativa === "" ? null : Number(form.area_privativa),
      area_total: form.area_total === "" ? null : Number(form.area_total),
      bedrooms: Number(form.bedrooms) || 0,
      suites: Number(form.suites) || 0,
      bathrooms: Number(form.bathrooms) || 0,
      parking: Number(form.parking) || 0,
      description: form.description,
      features: form.featuresText.split("\n").map((s) => s.trim()).filter(Boolean),
      status: form.status,
      featured: form.featured,
      published: form.published,
      map_url: form.map_url.trim(),
    };
    try {
      if (isEdit) {
        await api.put(`/admin/properties/${id}`, payload);
        toast.success("Imóvel atualizado.");
      } else {
        const { data } = await api.post("/admin/properties", payload);
        toast.success("Imóvel cadastrado. Agora adicione as fotografias.");
        refreshProperties();
        navigate(`/admin/imoveis/${data.id}/editar`, { replace: true });
        setSaving(false);
        return;
      }
      refreshProperties();
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !isEdit) return;
    setUploading(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post(`/admin/properties/${id}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImages(data.images || []);
      toast.success("Fotografias enviadas.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = async (img) => {
    try {
      const { data } = await api.post(`/admin/properties/${id}/photos/delete`, { image: img });
      setImages(data.images || []);
      toast.success("Fotografia removida.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50 pb-20" data-testid="property-form-page">
      <Seo title={`${isEdit ? "Editar" : "Novo"} Imóvel | Casa Vale`} description="Cadastro de imóvel." />
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-brand transition-colors" data-testid="form-back-link">
            <ArrowLeft className="w-4 h-4" /> Painel
          </Link>
          <h1 className="text-base font-bold text-slate-900">{isEdit ? "Editar Imóvel" : "Novo Imóvel"}</h1>
          <span className="w-16" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 mt-10 space-y-8">
        <form onSubmit={save} className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-6" data-testid="property-form">
          <div>
            <label htmlFor="f-title" className={labelCls}>Nome do imóvel *</label>
            <input id="f-title" required value={form.title} onChange={set("title")} className={inputCls} placeholder="Ex.: Residência Jardim do Vale" data-testid="form-title" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label htmlFor="f-type" className={labelCls}>Tipo</label>
              <select id="f-type" value={form.type} onChange={set("type")} className={inputCls} data-testid="form-type">
                <option value="casa">Casa</option>
                <option value="apartamento">Apartamento</option>
              </select>
            </div>
            <div>
              <label htmlFor="f-city" className={labelCls}>Cidade</label>
              <input id="f-city" value={form.city} onChange={set("city")} className={inputCls} data-testid="form-city" />
            </div>
            <div>
              <label htmlFor="f-neighborhood" className={labelCls}>Bairro</label>
              <input id="f-neighborhood" value={form.neighborhood} onChange={set("neighborhood")} className={inputCls} data-testid="form-neighborhood" />
            </div>
          </div>
<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
  <div>
    <label htmlFor="f-finalidade" className={labelCls}>Finalidade</label>
    <select
      id="f-finalidade"
      value={form.finalidade}
      onChange={set("finalidade")}
      className={inputCls}
    >
      <option value="venda">Venda</option>
      <option value="locacao">Locação</option>
      <option value="venda_locacao">Venda/Locação</option>
    </select>
  </div>

  <div>
    <label htmlFor="f-condominio" className={labelCls}>Condomínio (R$)</label>
    <input
      id="f-condominio"
      type="number"
      min="0"
      step="0.01"
      value={form.condominio}
      onChange={set("condominio")}
      className={inputCls}
      placeholder="Opcional"
    />
  </div>

  <div>
    <label htmlFor="f-iptu" className={labelCls}>IPTU (R$)</label>
    <input
      id="f-iptu"
      type="number"
      min="0"
      step="0.01"
      value={form.iptu}
      onChange={set("iptu")}
      className={inputCls}
      placeholder="Opcional"
    />
  </div>
</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <label htmlFor="f-price" className={labelCls}>Preço (R$)</label>
              <input id="f-price" type="number" min="0" value={form.price} onChange={set("price")} className={inputCls} placeholder="Vazio = sob consulta" data-testid="form-price" />
            </div>
            <div>
              <label htmlFor="f-areap" className={labelCls}>Área privativa (m²)</label>
              <input id="f-areap" type="number" min="0" value={form.area_privativa} onChange={set("area_privativa")} className={inputCls} data-testid="form-area-privativa" />
            </div>
            <div>
              <label htmlFor="f-areat" className={labelCls}>Área total (m²)</label>
              <input id="f-areat" type="number" min="0" value={form.area_total} onChange={set("area_total")} className={inputCls} data-testid="form-area-total" />
            </div>
            <div>
              <label htmlFor="f-bedrooms" className={labelCls}>Dormitórios</label>
              <input id="f-bedrooms" type="number" min="0" value={form.bedrooms} onChange={set("bedrooms")} className={inputCls} data-testid="form-bedrooms" />
            </div>
            <div>
              <label htmlFor="f-suites" className={labelCls}>Suítes</label>
              <input id="f-suites" type="number" min="0" value={form.suites} onChange={set("suites")} className={inputCls} data-testid="form-suites" />
            </div>
            <div>
              <label htmlFor="f-bathrooms" className={labelCls}>Banheiros</label>
              <input id="f-bathrooms" type="number" min="0" value={form.bathrooms} onChange={set("bathrooms")} className={inputCls} data-testid="form-bathrooms" />
            </div>
            <div>
              <label htmlFor="f-parking" className={labelCls}>Vagas de garagem</label>
              <input id="f-parking" type="number" min="0" value={form.parking} onChange={set("parking")} className={inputCls} data-testid="form-parking" />
            </div>
            <div>
              <label htmlFor="f-status" className={labelCls}>Situação</label>
              <select id="f-status" value={form.status} onChange={set("status")} className={inputCls} data-testid="form-status">
                <option value="disponivel">Disponível</option>
                <option value="reservado">Reservado</option>
                <option value="vendido">Vendido</option>
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="f-description" className={labelCls}>Descrição completa</label>
            <textarea id="f-description" rows={5} value={form.description} onChange={set("description")} className={inputCls} data-testid="form-description" />
          </div>

          <div>
            <label htmlFor="f-features" className={labelCls}>Características (uma por linha)</label>
            <textarea id="f-features" rows={4} value={form.featuresText} onChange={set("featuresText")} className={inputCls} placeholder={"Área gourmet\nPiscina\nPainéis solares"} data-testid="form-features" />
          </div>

          <div>
            <label htmlFor="f-map" className={labelCls}>Mapa (URL de incorporação do Google Maps, opcional)</label>
            <input id="f-map" type="url" value={form.map_url} onChange={set("map_url")} className={inputCls} placeholder="https://www.google.com/maps/embed?..." data-testid="form-map-url" />
          </div>

          <div className="flex flex-wrap gap-6">
            <label className="inline-flex items-center gap-2.5 text-sm font-semibold text-slate-700 cursor-pointer">
              <input type="checkbox" checked={form.featured} onChange={set("featured")} className="w-4 h-4 accent-[#00A65A]" data-testid="form-featured" />
              Destacar na página inicial
            </label>
            <label className="inline-flex items-center gap-2.5 text-sm font-semibold text-slate-700 cursor-pointer">
              <input type="checkbox" checked={form.published} onChange={set("published")} className="w-4 h-4 accent-[#00A65A]" data-testid="form-published" />
              Anúncio publicado
            </label>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full sm:w-auto bg-brand hover:bg-brand-dark disabled:opacity-60 text-white font-bold px-10 py-3.5 rounded-full transition-colors"
            data-testid="form-save-btn"
          >
            {saving ? "Salvando..." : isEdit ? "Salvar Alterações" : "Cadastrar Imóvel"}
          </button>
        </form>

        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8" data-testid="photos-panel">
          <h2 className="text-lg font-bold text-slate-900">Fotografias</h2>
          {!isEdit ? (
            <p className="text-sm text-slate-500 mt-2">Cadastre o imóvel primeiro para adicionar fotografias.</p>
          ) : (
            <>
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {images.map((img, i) => (
                  <div key={img} className="relative group rounded-xl overflow-hidden bg-slate-100" data-testid={`photo-item-${i}`}>
                    <img src={resolveImage(img)} alt={`Fotografia ${i + 1} do imóvel`} className="w-full aspect-[4/3] object-cover" />
                    <button
                      type="button"
                      onClick={() => removePhoto(img)}
                      className="absolute top-2 right-2 p-1.5 rounded-full bg-red-600 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label={`Remover fotografia ${i + 1}`}
                      data-testid={`photo-delete-${i}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    {i === 0 && (
                      <span className="absolute bottom-2 left-2 text-[10px] font-bold uppercase tracking-wider bg-brand text-white px-2 py-0.5 rounded-full">
                        Capa
                      </span>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="aspect-[4/3] rounded-xl border-2 border-dashed border-slate-300 hover:border-brand text-slate-400 hover:text-brand flex flex-col items-center justify-center gap-2 transition-colors"
                  data-testid="photo-upload-btn"
                >
                  <ImagePlus className="w-6 h-6" />
                  <span className="text-xs font-bold">{uploading ? "Enviando..." : "Adicionar fotos"}</span>
                </button>
              </div>
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={upload} data-testid="photo-file-input" />
              <p className="text-xs text-slate-400 mt-3">JPG, PNG ou WebP até 10MB. A primeira fotografia é usada como capa do anúncio.</p>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
